"use client";

import { useCallback, useEffect, useState } from "react";
import {
  ATTENTION_COOLDOWN_HOURS,
  ATTENTION_DISMISSED_STORAGE_KEY,
  ATTENTION_DURATION,
  ATTENTION_INITIAL_DELAY,
  ATTENTION_INTERVAL,
} from "./constants";
import { ATTENTION_MESSAGES } from "./types";
import { CedeIcon } from "@/components/icons/library/brand/CedeIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface FeedbackBubbleProps {
  onClick: () => void;
  isFormOpen: boolean;
  isDragging?: boolean;
}

export function FeedbackBubble({ onClick, isFormOpen, isDragging }: FeedbackBubbleProps) {
  const [showAttention, setShowAttention] = useState(false);
  const [attentionMessage, setAttentionMessage] = useState("");
  const [isShaking, setIsShaking] = useState(false);

  const shouldShowAttention = useCallback(() => {
    if (typeof window === "undefined") return false;
    const dismissed = localStorage.getItem(ATTENTION_DISMISSED_STORAGE_KEY);
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      // Only show again after cooldown period
      if (now.getTime() - dismissedDate.getTime() < ATTENTION_COOLDOWN_HOURS * 60 * 60 * 1000) {
        return false;
      }
    }
    return true;
  }, []);

  const triggerAttention = useCallback(() => {
    if (isFormOpen || !shouldShowAttention()) return;

    const randomIndex = Math.floor(Math.random() * ATTENTION_MESSAGES.length);
    setAttentionMessage(ATTENTION_MESSAGES[randomIndex] ?? ATTENTION_MESSAGES[0]);
    setIsShaking(true);
    setShowAttention(true);

    // Stop shaking after animation
    setTimeout(() => setIsShaking(false), 600);

    // Hide message after duration
    setTimeout(() => setShowAttention(false), ATTENTION_DURATION);
  }, [isFormOpen, shouldShowAttention]);

  const dismissAttention = useCallback(() => {
    setShowAttention(false);
    localStorage.setItem(ATTENTION_DISMISSED_STORAGE_KEY, new Date().toISOString());
  }, []);

  useEffect(() => {
    // Initial attention after delay
    const initialTimeout = setTimeout(() => {
      triggerAttention();
    }, ATTENTION_INITIAL_DELAY);

    // Periodic attention
    const interval = setInterval(() => {
      triggerAttention();
    }, ATTENTION_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [triggerAttention]);

  // Hide attention when form opens
  useEffect(() => {
    if (isFormOpen) {
      setShowAttention(false);
    }
  }, [isFormOpen]);

  return (
    <div className="relative">
      {/* Attention Message Bubble */}
      <div
        className={cn(
          "absolute bottom-full right-0 mb-2 whitespace-nowrap",
          "bg-cede-bg-secondary border border-cede-border-container rounded-xl px-4 py-2",
          "text-sm text-cede-text-primary font-medium",
          "transition-all duration-300 ease-out",
          "shadow-lg shadow-black/20",
          showAttention && !isFormOpen
            ? "opacity-100 translate-x-0 translate-y-0"
            : "opacity-0 translate-x-4 translate-y-2 pointer-events-none",
        )}
      >
        {attentionMessage}
        {/* Speech bubble tail */}
        <div className="absolute -bottom-2 right-4 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-cede-bg-secondary" />
        {/* Dismiss button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            dismissAttention();
          }}
          className="absolute -top-2 -right-2 w-5 h-5 bg-cede-bg-primary border border-cede-border-container rounded-full flex items-center justify-center text-cede-text-secondary hover:text-cede-text-primary hover:bg-cede-bg-secondary transition-colors"
          aria-label="Dismiss"
        >
          <span className="text-xs leading-none">&times;</span>
        </button>
      </div>

      {/* Panda Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center",
              "bg-cede-bg-secondary border border-cede-border-container",
              "shadow-lg shadow-black/30",
              "transition-all duration-300 ease-out",
              "hover:scale-110 hover:shadow-xl hover:shadow-black/40",
              "hover:border-cede-primary/50",
              "focus:outline-none focus:ring-2 focus:ring-cede-primary/50",
              "active:scale-95",
              isShaking && !isDragging && "animate-feedback-shake",
              !isShaking && !isFormOpen && !isDragging && "animate-feedback-pulse",
            )}
            aria-label="Send feedback"
          >
            <span className="translate-x-px translate-y-px">
              <CedeIcon width={32} height={32} />
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">Send Feedback</TooltipContent>
      </Tooltip>
    </div>
  );
}
